import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Close } from '@material-ui/icons';
import { Divider, IconButton, List, ListItem, ListItemText, makeStyles, Menu } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isSafari } from 'react-device-detect';

import { focusOnFirstElement, joinClassNames } from '../../../util/util';
import { MenuUtils } from './MenuUtils';
import { useConfiguration } from '../../../contexts/ConfigurationContext';

const AUTO_RESOLUTION = { value: 'Auto', label: 'Auto' };

const PLAYBACK_SPEEDS = [
	{ value: 0.25, label: '0.25x' },
	{ value: 0.5, label: '0.5x' },
	{ value: 0.75, label: '0.75x' },
	{ value: 1, label: 'PLAYBACK_SPEED_NORMAL' },
	{ value: 1.25, label: '1.25x' },
	{ value: 1.5, label: '1.5x' },
	{ value: 1.75, label: '1.75x' },
	{ value: 2, label: '2x' },
];
interface QualitySettingMenuProps {
	playerConfigs?: any;
	isMobile?: boolean;
	sideWindowOpen?: boolean;
	className?: string;
}

const useListCustomStyle = makeStyles(() => ({
	listItem: {
		minWidth: '190px',
	},
	listItemFocus: {
		outline: '1px dash white',
	},
	listItemIcon: {
		fontSize: '1em',
	},
	primaryListText: {
		fontSize: '0.75em',
		lineHeight: 1.5,
		fontFamily: 'inherit',
		textTransform: 'none',
	},
	focusButton: {
		'&:focus': {
			color: '#ffffff',
		},
	},
}));

export const QualitySettingMenu: React.FC<QualitySettingMenuProps> = ({ playerConfigs, className, isMobile }) => {
	const { t } = useTranslation();
	const { openSettingMenu, mediasData, currentMediaId, playlistId, isMediaLive, mediaType, configParams, isFullScreen, setOpenSettingMenu } = useConfiguration();

	const [selectedQualityValue, setSelectedQualityValue] = useState(AUTO_RESOLUTION.label);
	const [selectedPlaybackSpeed, setSelectedPlaybackSpeed] = useState(1);
	const [hoveringItem, setHoveringItem] = useState('');

	const [selectedMenu, setSelectedMenu] = useState('');
	const [selectedMenuHoveringItem, setSelectedMenuHoveringItem] = useState('');

	const container = !configParams?.qplayerPreview ? configParams?.container ?? document : document;
	const playerId = useMemo(() => `player-${playlistId || currentMediaId}-${configParams.containerKey}`, [playlistId, currentMediaId, configParams.containerKey]);
	const classes = useListCustomStyle();

	const menuItems = useMemo(() => {
		const currentMedia = mediasData.find(({ id, originalId }: any) => id === currentMediaId || id === originalId) ?? mediasData?.[0];
		const stream = ((currentMedia?.mediaDetails ?? [])[0]?.streams ?? []).find((s: any) => s.videos.length > 0);
		if (!stream || stream.videos.length === 0) {
			return [];
		}
		return stream.videos;
	}, [currentMediaId, mediasData]);

	const onCloseMenu = useCallback(() => {
		setOpenSettingMenu(null);
		setHoveringItem('');
		setSelectedMenu('');

		if (isMobile) {
			const dialogWrapper = container.querySelector('.bottomControlbarSettings') as HTMLElement;
			dialogWrapper?.focus();
			setTimeout(() => {
				const dialogWrapper = container.querySelector('.bottomControlbarSettings') as HTMLElement;
				dialogWrapper?.focus();
			}, 100);
		}

	}, [isMobile, container, setOpenSettingMenu]);

	const changeVideoQuality = useCallback(
		(item: any) => {
			if (item.label === selectedQualityValue) {
				return;
			}
			setSelectedQualityValue(item.label);
			(window as any).GoBrain.widgets(playerId)?.quality(item);
		},
		[selectedQualityValue, playerId]
	);

	const changePlaybackSpeed = useCallback(
		(item: any) => {
			if (item.value === selectedPlaybackSpeed) {
				return;
			}
			setSelectedPlaybackSpeed(item.value);
			(window as any).GoBrain.widgets(playerId).playbackRate(item.value);
		},
		[selectedPlaybackSpeed, playerId]
	);

	const onMenuItemClick = useCallback(
		(_e: React.MouseEvent, item: any, type = 'quality') => {
			if (type === 'quality') {
				changeVideoQuality(item);
			} else {
				changePlaybackSpeed(item);
			}
		},
		[changeVideoQuality, changePlaybackSpeed]
	);

	const onKeyDown = useCallback(
		(event, item?: any, type = 'quality') => {
			if (event.key === 'Tab') {
				event.stopPropagation();
				return;
			}

			if (event.key === 'Enter' || event.key === ' ') {
				if (type === 'quality') {
					changeVideoQuality(item);
				} else {
					changePlaybackSpeed(item);
				}
				event.stopPropagation();
			}
		},
		[changeVideoQuality, changePlaybackSpeed]
	);

	const handleKeydownInSettingMenu = (event: any, type: any) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') {
			setSelectedMenu(type);
		}
	};

	const onSmallScreenKeyDown = (e: any) => {
		if (e.key === 'Escape') {
			onCloseMenu();
		}
	}

	useEffect(() => {
		if (openSettingMenu?.type === 'quality') {
			return;
		}
		return () => {
			setHoveringItem('');
			setSelectedMenu('');
		};
	}, [openSettingMenu]);

	const focusOnDialog = useCallback(() => {
		if (!isMobile) {
			return;
		}
		setTimeout(() => {
			const dialogWrapper = container.querySelector('#quality-dialog') as HTMLElement;
			dialogWrapper?.focus();
		}, 200);
	}, [isMobile, container]);

	const onGoBack = useCallback(() => {
		setSelectedMenu('');
		focusOnDialog();
	}, [focusOnDialog]);

	useEffect(() => {
		if (openSettingMenu?.type === 'quality') {
			focusOnDialog();
		}
	}, [focusOnDialog, isMobile, openSettingMenu, selectedMenu]);

	useEffect(() => {
		if (openSettingMenu?.type !== 'quality') {
			return;
		}

		if (openSettingMenu?.isKeyboardTriggered) {
			focusOnFirstElement();
		} else {
			setSelectedMenuHoveringItem('');
		}
	}, [openSettingMenu?.type, selectedMenu, openSettingMenu?.isKeyboardTriggered]);

	const renderSettingMenuItems = () => {
		const listItemClass = classes.listItem;
		const showPlaybackSpeedSettings = playerConfigs.bottomControls?.showPlaybackSpeedSettings && !isMediaLive && mediaType !== 'scenario';
		const showAllMenus = playerConfigs.bottomControls?.showVideoQualitySettings && menuItems.length > 0 && showPlaybackSpeedSettings;

		return (
			<>
				{!selectedMenu && showAllMenus && (
					<>
						{isMobile && (
							<>
								<ListItem className={listItemClass} component={"div"} autoFocus>
									<ListItemText
										classes={{ primary: classes.primaryListText }}
										primary={t('SETTINGS')}
										id="quality-sub-header"
									/>
									<IconButton
										aria-label={t('LABEL_CLOSE')}
										style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
										onClick={onCloseMenu}
										className={classes.focusButton}
									>
										<Close className={joinClassNames(classes.listItemIcon, isSafari ? 'safari-fix' : '')} htmlColor="#fff" aria-hidden="true" />
									</IconButton>
								</ListItem>

								<Divider
									tabIndex={-1}
									aria-hidden="true"
									style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '0 5px 3px' }}
								/>
							</>
						)}
						<List
							className={isMobile ? 'qplayer__setting-menu--mobile-scrollable' : ''}
							role="menu"
							aria-labelledby={"quality-sub-header"}
						>
							<MenuUtils.MenuSelector
								menuType="playbackSpeed"
								itemLabel={t('PLAYBACK_SPEED')}
								isMobile={isMobile}
								hoveringItem={selectedMenuHoveringItem}
								playerConfigs={playerConfigs}
								onSelectMenu={setSelectedMenu}
								onKeyDown={handleKeydownInSettingMenu}
								onCloseMenu={onCloseMenu}
								onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
								onLeaveItem={() => setSelectedMenuHoveringItem("")}
							/>

							{menuItems.length > 0 && (
								<MenuUtils.MenuSelector
									menuType="quality"
									itemLabel={t('QUALITY')}
									isMobile={isMobile}
									hoveringItem={selectedMenuHoveringItem}
									playerConfigs={playerConfigs}
									onSelectMenu={setSelectedMenu}
									onKeyDown={handleKeydownInSettingMenu}
									onCloseMenu={onCloseMenu}
									onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
									onLeaveItem={() => setSelectedMenuHoveringItem("")}
								/>
							)}
						</List>
					</>
				)}

				{(selectedMenu === 'playbackSpeed' ||
					(showPlaybackSpeedSettings && (!playerConfigs.bottomControls?.showVideoQualitySettings || menuItems.length === 0))) && (
						<MenuUtils.MenuPanel
							menuType="playbackSpeed"
							items={PLAYBACK_SPEEDS}
							isMobile={isMobile}
							hoveringItem={hoveringItem}
							selectedItem={selectedPlaybackSpeed}
							playerConfigs={playerConfigs}
							showAllMenus={showAllMenus}
							selectedMenu={selectedMenu}
							onSelectMenu={setSelectedMenu}
							onMenuItemClick={onMenuItemClick}
							onKeyDown={onKeyDown}
							onCloseMenu={onCloseMenu}
							onHoverItem={(lang: any) => setHoveringItem(lang)}
							onLeaveItem={() => setHoveringItem("")}
							onGoBack={onGoBack}
							container={container}
						/>
					)}

				{(selectedMenu === 'quality' || !showPlaybackSpeedSettings) && (
					<MenuUtils.MenuPanel
						menuType="quality"
						items={menuItems}
						isMobile={isMobile}
						hoveringItem={hoveringItem}
						selectedItem={selectedQualityValue}
						playerConfigs={playerConfigs}
						showAllMenus={showAllMenus}
						selectedMenu={selectedMenu}
						onSelectMenu={setSelectedMenu}
						onMenuItemClick={onMenuItemClick}
						onKeyDown={onKeyDown}
						onCloseMenu={onCloseMenu}
						onHoverItem={(lang: any) => setHoveringItem(lang)}
						onLeaveItem={() => setHoveringItem("")}
						onGoBack={onGoBack}
						container={container}
					/>
				)}
			</>
		);
	};

	if (isMobile) {
		return (
			<dialog
				id="quality-dialog"
				className="qplayer__setting-menu qplayer__setting-menu--mobile-wrapper"
				onClose={onCloseMenu}
				tabIndex={0}
				open={openSettingMenu?.type === 'quality'}
				aria-modal="true"
				aria-hidden={openSettingMenu?.type !== 'quality'}
				onKeyDown={onSmallScreenKeyDown}
			>
				<div className="qplayer__setting-menu--mobile" onMouseDown={(e) => e.stopPropagation()}>
					{renderSettingMenuItems()}
				</div>
			</dialog>
		);
	}
	const anchorElem = container.querySelector('.bottomControlbarSettings') ?? openSettingMenu?.anchor ?? container.querySelector('.bottomControlbar');
	const bottomBar = container.querySelector('.bottomControlbar');
	let menuMaxHeight = "calc(100vh - 100px)";
	if (!configParams?.qplayerPreview) {
		const playerWindow = configParams.container.querySelector(".playerWindow");
		const qplayerHeight = playerWindow ? playerWindow.getBoundingClientRect()?.height || 0 : 0;
		menuMaxHeight = `calc(${qplayerHeight}px - ${bottomBar ? bottomBar.getBoundingClientRect()?.height + 30 : 100}px)`;
	}

	return openSettingMenu?.type === 'quality' ? (
		<Menu
			onMouseDown={(e) => e.stopPropagation()}
			style={{ zIndex: configParams?.isModalInstance ? 90000 : 30001, }}
			key={'quality-menu'}
			className={joinClassNames('qplayer__setting-menu', className)}
			open={openSettingMenu?.type === 'quality'}
			onClose={onCloseMenu}
			getContentAnchorEl={null}
			anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			anchorEl={anchorElem || openSettingMenu?.anchor || bottomBar}
			disablePortal={!(configParams.isModalInstance && !isFullScreen)}
			PaperProps={{
				style: {
					transform: 'translateY(-30px)',
					maxHeight: menuMaxHeight
				},
				className: 'closeable-by-tab',
			}}
			MenuListProps={{
				'aria-labelledby': 'quality-sub-header',
			}}
		>
			<div
				tabIndex={-1}
				onKeyDown={(e) => {
					if (e.key.includes('Arrow')) {
						e.stopPropagation();
						e.preventDefault();
					}
				}}
			>
				{renderSettingMenuItems()}
			</div>
		</Menu>
	) : null;
};
